'use client';

import { get as _get, pick as _pick } from 'lodash-es';
import { useMemo, useCallback, useRef, Fragment } from 'react';
import { useMutation, useQuery } from '@apollo/client/react';
import { useSearchParams, useRouter } from 'next/navigation';
import { Formik } from 'formik';
import type { FormikProps, FormikHelpers } from 'formik';
import moment from 'moment';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import { makeStyles } from 'tss-react/mui';
import EventForm, { draftEventSchema } from '@/components/events/form';
import type { EventFormValues } from '@/components/events/form';
import { createEventMutation, copyEventQuery } from '@/apollo/events';
import { eventPath } from '@/utils/slugify';
import { primaryCityQuery } from '@/apollo/cities';
import Loading from '@/components/loading';
import ErrorMessage from '@/components/error-message';
import LoginRequiredNotice from '@/components/login-required-notice';
import MaterialLink from '@/components/material-link';
import PublishDialog from '@/components/events/publish-dialog';
import EventFormActions from '@/components/events/event-form-actions';
import DraftAutosave from '@/components/forms/draft-autosave';
import { processMutationError } from '@/utils/formik';
import Main from '@/components/main';
import { momentParse, combineDateAndTime } from '@/utils/time';
import { useAppContext } from '@/contexts/app';
import { usePublishFlow } from '@/components/events/use-publish-flow';

const useStyles = makeStyles()((theme) => ({
  loginRequiredNotice: {
    margin: theme.spacing(2, 0),
  },
}));

const EventNewPageClient = () => {
  const searchParams = useSearchParams();
  const copyEventId = searchParams.get('copyEventId');
  const groupId = searchParams.get('groupId');
  const { isLoggedIn, loggedInUser } = useAppContext();
  const { classes } = useStyles();
  const router = useRouter();
  const formikRef = useRef<FormikProps<EventFormValues>>(null);

  const draftKey = useMemo(() => {
    if (!loggedInUser?.id) return null;
    const prefix = `user-${loggedInUser.id}:event-draft-new`;
    if (copyEventId) return `${prefix}-copy-${copyEventId}`;
    if (groupId) return `${prefix}-group-${groupId}`;
    return prefix;
  }, [loggedInUser?.id, copyEventId, groupId]);

  const hydrateEventDraft = useCallback((raw: Record<string, unknown>) => {
    const toMoment = (v: unknown) => (v ? moment(v as string) : null);
    return {
      ...raw,
      startDate: toMoment(raw.startDate),
      startTime: toMoment(raw.startTime),
      endDate: toMoment(raw.endDate),
      endTime: toMoment(raw.endTime),
    };
  }, []);

  const {
    publishDialogOpen,
    handleOpenPublishDialog,
    handlePublishConfirm,
    handleClosePublishDialog,
    consumeStatusOverride,
  } = usePublishFlow(formikRef);

  const {
    data: copyEventData,
    loading: copyEventLoading,
    error: copyEventError,
  } = useQuery(copyEventQuery, {
    variables: {
      id: copyEventId,
    },
    fetchPolicy: 'network-only',
    skip: !copyEventId,
    ssr: false,
  });
  const copyEvent = _get(copyEventData, 'event');

  const {
    data: primaryCityData,
    loading: primaryCityLoading,
    error: primaryCityError,
  } = useQuery(primaryCityQuery, { ssr: false });
  const primaryCity = _get(primaryCityData, 'me.primaryCity');

  const [createEvent] = useMutation(createEventMutation);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- dates are Moment objects at runtime but typed as Date
  const initialValues = useMemo<any>(() => {
    if (copyEvent) {
      let startDate;
      let startTime;
      let endDate = null;
      let endTime = null;

      if (copyEvent.startAt) {
        // Advance to the next occurrence of the same day-of-week,
        // preserving the original time (e.g. Wed 6:30 PM → next Wed 6:30 PM)
        const sourceStart = moment(copyEvent.startAt);
        const nextStart = momentParse()
          .day(sourceStart.day())
          .hour(sourceStart.hour())
          .minute(sourceStart.minute())
          .second(0);
        // If that day-of-week already passed this week (or is today), push to next week
        if (nextStart.isSameOrBefore(momentParse(), 'day')) {
          nextStart.add(1, 'week');
        }
        startDate = nextStart;
        startTime = moment(nextStart);

        // Preserve the original duration between start and end
        if (copyEvent.endAt) {
          const duration = moment(copyEvent.endAt).diff(sourceStart);
          const nextEnd = moment(nextStart).add(duration);
          endDate = nextEnd;
          endTime = moment(nextEnd);
        }
      } else {
        // Source event had no date (e.g. a draft) — fall back to next 7 PM
        const defaultStart = momentParse().hour(19).minutes(0).seconds(0);
        if (defaultStart.isBefore(momentParse())) {
          defaultStart.add(1, 'day');
        }
        startDate = defaultStart;
        startTime = moment(defaultStart);
      }

      return {
        ..._pick(copyEvent, [
          'hostType',
          'eventType',
          'registrationType',
          'name',
          'description',
          'startAtDetails',
          'startLocation',
          'startLocationAddress',
          'startLocationDetails',
          'loop',
          'pace',
          'distance',
          'regroup',
          'terrain',
          'weatherCancellation',
          'ageRestriction',
          'audienceRestriction',
          'endLocation',
          'endLocationAddress',
          'endLocationDetails',
          'infoUrl',
          'routeUrl',
          'categoryIds',
          'visibility',
          'approvalPrompt',
          'approvalDescription',
          'contactName',
          'contactPhoneNumber',
          'contactEmail',
        ]),
        startDate,
        startTime,
        endDate,
        endTime,
        currentImage: copyEvent.image,
        status: 'DRAFT',
        groupId: _get(copyEvent, 'group.id'),
      };
    }

    const startAt = momentParse().hour(19).minutes(0).seconds(0);
    if (startAt.isBefore(momentParse())) {
      startAt.add(1, 'day');
    }
    return {
      eventType: 'RIDE',
      status: 'DRAFT',
      hostType: 'User',
      groupId: groupId || null,
      visibility: groupId ? 'GROUP' : 'PUBLIC',
      startDate: startAt,
      startTime: startAt,
      categoryIds: [],
      ageRestriction: 'GENERAL',
      registrationType: 'OPEN',
    };
  }, [copyEvent, groupId]);

  const handleSubmit = useCallback((values: EventFormValues, actions: FormikHelpers<EventFormValues>) => {
    const effectiveValues = consumeStatusOverride(values);
    createEvent({
      variables: {
        data: {
          ..._pick(effectiveValues, [
            'hostType',
            'groupId',
            'eventType',
            'registrationType',
            'name',
            'description',
            'startAtDetails',
            'startLocation',
            'startLocationAddress',
            'startLocationDetails',
            'loop',
            'endLocation',
            'endLocationAddress',
            'endLocationDetails',
            'infoUrl',
            'routeUrl',
            'categoryIds',
            'visibility',
            'pace',
            'distance',
            'regroup',
            'terrain',
            'weatherCancellation',
            'ageRestriction',
            'audienceRestriction',
            'approvalPrompt',
            'approvalDescription',
            'contactName',
            'contactPhoneNumber',
            'contactEmail',
            'image',
            'status',
          ]),
          startAt: combineDateAndTime(effectiveValues.startDate, effectiveValues.startTime),
          endAt: combineDateAndTime(effectiveValues.endDate, effectiveValues.endTime),
          copyImageEventId: copyEvent && effectiveValues.currentImage ? copyEvent.id : null,
        },
        citySlug: primaryCity.slug,
      },
    }).then(({ data: { createEvent: event } }) => {
      if (draftKey) {
        window.localStorage.removeItem(draftKey);
      }
      actions.setStatus('submitted');
      if (effectiveValues.status === 'DRAFT') {
        router.replace(`${eventPath(event.id, event.name)}/edit`);
      } else {
        router.push(eventPath(event.id, event.name));
      }
    }).catch((error: unknown) => {
      processMutationError(error, actions);
    });
  }, [primaryCity, copyEvent, createEvent, router, consumeStatusOverride, draftKey]);

  const loading = primaryCityLoading || copyEventLoading;

  return (
    <Main compact>
      <Typography variant="h1" sx={{ my: 3 }}>Add Event</Typography>

      {!isLoggedIn && (
        <LoginRequiredNotice
          returnTo="/events/new"
          action="create an event"
          className={classes.loginRequiredNotice}
        />
      )}

      {copyEvent && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Copied from <strong>{copyEvent.name}</strong>. Review the details below &mdash; especially the date.
        </Alert>
      )}

      <Alert severity="info" sx={{ mb: 3 }}>
        See <MaterialLink href="/about" target="_blank">About Everyday Rides</MaterialLink> for info about the calendar and using the site. See the <MaterialLink href="/group-ride-guide" target="_blank">Group Ride Guide</MaterialLink> for tips on planning rides. Planning a recurring ride? Create your first event, then convert it to a series from the event page.
      </Alert>

      {(primaryCityError || copyEventError) ? (
        <ErrorMessage
          message={(primaryCityError || copyEventError)!.message}
          error={(primaryCityError || copyEventError)!}
        />
      ) : loading ? (
        <Loading />
      ) : (
        <Formik
        innerRef={formikRef}
        initialValues={initialValues}
        validationSchema={draftEventSchema}
        onSubmit={handleSubmit}
      >
        <Fragment>
          <DraftAutosave
            name={draftKey}
            excludeFields={['image', 'currentImage']}
            hydrate={hydrateEventDraft}
          />
          <EventForm
            disabled={!isLoggedIn}
          />
          <EventFormActions eventStatus="DRAFT" onPublish={handleOpenPublishDialog} />
          <PublishDialog
            open={publishDialogOpen}
            onClose={handleClosePublishDialog}
            onConfirm={handlePublishConfirm}
          />
        </Fragment>
      </Formik>
      )}
    </Main>
  );
};

export default EventNewPageClient;
